import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    SoundType,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../AnimationContents';
import { GameLogic } from '../Logic/GameLogic';
import { GameModel, GameStateEnum } from '../Models/GameModel';
import { MiniGameTypeEnum } from '../Models/MiniGameModel';
import { GameController } from './GameController';
import { RopesGameController } from './MiniGames/RopesGame/RopesGameController';
import { QuestScreenController } from './UI/Dialog/QuestScreenController';
import { GamePadContainerController } from './UI/GamePad/GamePadContainerController';
import { InfoScreenController } from './UI/InfoScreen/InfoScreenController';
import { MiniMapController } from './UI/MiniMapScreen/MiniMapController';
import { TaskListScreenController } from './UI/TaskList/TaskListScreenController';
import { UIController } from './UI/UIController';
import { UIOverlay } from './UI/UIOverlay';
import { LaserGameController } from './MiniGames/LaserGame/LaserGameController';
import { FuelGameController } from './MiniGames/FuelGame/FuelGameController';
import { EngineGameController } from './MiniGames/EngineGame/EngineGameController';
import { TooltipController } from './UI/Dialog/TooltipController';
import { DialogScreenController, IDialogScreenControllerOptions } from './UI/Dialog/DialogScreenController';
import { SoundConstants, SoundLevels } from '../Constants/SoundConstants';
import { RescueGameController } from './MiniGames/RescueGame/RescueGameController';
import { PropsGameController } from './MiniGames/PropsGame/PropsGameController';
import { ButtonController } from '../Common/ButtonController';
import { IExitButtonControllerOptions, UIExitButtonController } from './UI/UIExitButtonController';

export interface IGameScreenControllerOptions {
}

export class GameScreenController extends AnimationBaseController {
    $model: GameModel;

    public constructor(
        @Inject private _options: IGameScreenControllerOptions,
        @Inject private _gameLogic: GameLogic,
    ) {
        super('GameScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.GAME_SCREEN_JSON,
            libraryName: 'root',
            sounds: [
                {
                    id: SoundConstants.LVL_AMBIENT,
                    type: SoundType.Music,

                },
                {
                    id: SoundConstants.WALKBY1,
                    type: SoundType.SFX,

                },
                {
                    id: SoundConstants.MINIGAME_COMPLETE,
                    type: SoundType.SFX,

                },
                {
                    id: SoundConstants.MINIGAME_LOOSE,
                    type: SoundType.SFX,

                },
            ],
            modelWatch: {
                showDialogScreen: async (value: boolean) => {
                    await this.removeChildControllerByNameAsync('DialogScreen');

                    if (value) {
                        await this.addChildControllerAsync(
                            {
                                controller: DialogScreenController,
                                model: this.$model,
                                options: {
                                    ...this.$model.showDialogScreenText,
                                    onComplete: () => {
                                        if (this.$model.showDialogScreenCallback) {
                                            this.$model.showDialogScreenCallback();
                                        }
                                        this._gameLogic.hideDialogScreen();

                                    },
                                },
                            },
                            false, 'DialogScreen',
                        );
                    }
                },
                showMiniGame: (showMiniGame: boolean) => {
                    this.removeChildControllerByName('UIExitButton');
                    let showExitButton = false;
                    if (showMiniGame) {
                        let view = null;
                        let model = null;

                        switch (this.$model.currMiniGameType) {
                            case MiniGameTypeEnum.ropes:
                                view = RopesGameController;
                                model = this.$model.RopesModel;
                                showExitButton = true;
                                break;

                            case MiniGameTypeEnum.laser:
                                view = LaserGameController;
                                model = this.$model.LaserModel;
                                showExitButton = true;
                                break;

                            case MiniGameTypeEnum.engine:
                                view = EngineGameController;
                                model = this.$model.EnginePowerUpModel;
                                showExitButton = true;
                                break;

                            case MiniGameTypeEnum.fuel:
                                view = FuelGameController;
                                model = this.$model.FuelModel;
                                showExitButton = true;
                                break;

                            case MiniGameTypeEnum.props:
                                view = PropsGameController;
                                model = this.$model.PropsModel;
                                break;

                            case MiniGameTypeEnum.rescue:
                                view = RescueGameController;
                                model = this.$model.RescueModel;
                                break;
                        }

                        this.addChildController(
                            {
                                controller: view,
                                model: model,
                                options: {
                                    onComplete: () =>
                                        this._gameLogic.onMiniGameComplete(
                                            this.$model.currMiniGameType,
                                        ),
                                },
                            },
                            'MiniGameScreen',
                        );
                        if (showExitButton)
                        this.addChildController(
                            {
                                controller: UIExitButtonController,
                                model: this.$model,
                                options: <IExitButtonControllerOptions>{
                                    onClick: ()=>{
                                        this.removeChildControllerByName('UIExitButton');
                                        this._gameLogic.resetGameScreen();
                                        this._gameLogic.resetAllMinigames();
                                        
                                    }
                                },
                            },
                            "UIExitButton"
                        );

                    } else {
                        this.removeChildControllerByName('MiniGameScreen');
                    }
                },
                showTooltipScreen: async (value: boolean) => {
                    if (value) {
                        await this.addChildControllerAsync(
                            {
                                controller: TooltipController,
                                model: this.$model,
                                options: {
                                    text: this.$model.showTooltipScreenText,
                                    isDone: this.$model.showTooltipDone,
                                },
                            },
                            false, 'TooltipScreen',
                        );
                    } else {
                        this.removeChildControllerByName('TooltipScreen');
                    }
                },
                playDoneSound: (done) => {
                    if (done) {
                        this.soundManager.playSound(SoundConstants.MINIGAME_COMPLETE, 0, SoundLevels.MINIGAME_ACTION);
                        this._gameLogic.setPlayDoneSound(false);
                    }

                },
                playLooseSound: (done) => {
                    if (done) {
                        this.soundManager.playSound(SoundConstants.MINIGAME_LOOSE, 0, SoundLevels.MINIGAME_ACTION);
                        this._gameLogic.setPlayLooseSound(false);
                    }

                },
                gameState: async (state: GameStateEnum) => {
                    await this.removeChildControllerByNameAsync('gameStateScreen');
                    
                    switch (state) {
                        case GameStateEnum.quest:
                            await this.addChildControllerAsync(
                                {
                                    controller: QuestScreenController,
                                    model: this.$model,
                                },
                                false, 'gameStateScreen',
                            );
                            break;

                        case GameStateEnum.minimap:
                            await this.addChildControllerAsync(
                                {
                                    controller: MiniMapController,
                                    model: this.$model,
                                    options: {
                                        gameController:
                                            this.$childControllers.get(
                                                'GameController',
                                            ),
                                    },
                                },
                                false, 'gameStateScreen',
                            );
                            break;

                        case GameStateEnum.info:
                            await this.addChildControllerAsync(
                                {
                                    controller: InfoScreenController,
                                    model: this.$model,
                                },
                                false, 'gameStateScreen',
                            );
                            break;

                        case GameStateEnum.task:
                            await this.addChildControllerAsync(
                                {
                                    controller: TaskListScreenController,
                                    model: this.$model,
                                },
                                false, 'gameStateScreen',
                            );
                            break;
                    }
                },

            },
        };
    }

    protected async onInitialize() {
        this.soundManager.play(SoundConstants.LVL_AMBIENT, true, SoundLevels.LVL_AMBIENT);

        await this.startGame();
    }

    protected onLandscape(): void {
    }

    protected onPortrait(): void {
    }

    protected onUpdate(frameRate: number): void {
        this._gameLogic.update(frameRate);
        /*
        if ((this.$model.showHurryUpScreen) && (this.$model.gameState === GameStateEnum.none)) {
            this._gameLogic.showTooltip();
        }
         */
        this.walkingSound();
    }

    private walkingSound() {
        if (!this.soundManager.isSoundPlaying(SoundConstants.WALKBY1)) {
            const unit = this.$model.unitList.firstOrDefault(ch => !!ch.player);
            if (unit.move[0] || unit.move[1]) {
                this.soundManager.playSound(SoundConstants.WALKBY1, 0, SoundLevels.WALKING);
            }
        }
    }

    private async startGame(): Promise<void> {
        this._gameLogic.reset();

        if (this.$childControllers.get('GameController')) {
            await this.removeChildControllerByNameAsync('GameController');
        }
        if (this.$childControllers.get('UIController')) {
            await this.removeChildControllerByNameAsync('UIController');
        }
        if (this.$childControllers.get('GamePadContainerController')) {
            await this.removeChildControllerByNameAsync('GamePadContainerController');
        }
        if (this.$childControllers.get('UIOverlay')) {
            await this.removeChildControllerByNameAsync('UIOverlay');
        }

        await this.addChildControllerAsync(
            {
                instanceName: 'map',
                controller: GameController,
                model: this.$model,
            },
            false,
            'GameController',
        );

        await this.addChildControllerAsync(
            {
                controller: UIController,
                model: this.$model,
            },
            false,
            'UIController',
        );

        await this.addChildControllerAsync(
            {
                controller: GamePadContainerController,
                model: this.$model.gamePad,
            },
            false,
            'GamePadContainerController',
        );

        await this.addChildControllerAsync(
            {
                controller: UIOverlay,
                model: this.$model,
            },
            false,
            'UIOverlay',
        );

        this._gameLogic.init();
    }
}
